import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Assignment as MissionIcon,
  Store as MarketIcon,
  Map as MapIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  BrightnessAuto as AutoIcon,
  Chat as ChatIcon,
  RocketLaunch as ShipIcon,
  LocationOn as LocationIcon,
  Terminal as LogsIcon,
  Storage as DbIcon,
  Cloud as R2Icon,
  People as NPCsIcon,
  Build as RepairsIcon,
  Warning as WarningIcon,
  Description as PromptIcon,
  Search as SearchIcon,
  Mic as VoiceIcon,
  History as HistoryIcon,
  SportsEsports as GameIcon,
} from '@mui/icons-material';
import { useConsole, type PanelType } from '../../contexts/ConsoleContext';
import type { ThemeMode } from '../../App';
import { BUILD_DATE } from '../../version';
import { NotificationSettings } from '../settings/NotificationSettings';
import { uiLogger } from '../../services/uiLogger';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  themeMode: ThemeMode;
  onThemeModeChange: (mode: ThemeMode) => void;
  onDebugToggle: () => void;
}

const DRAWER_WIDTH = 280;

export function MobileDrawer({
  open,
  onClose,
  themeMode,
  onThemeModeChange,
  onDebugToggle,
}: MobileDrawerProps) {
  const consoleCtx = useConsole();

  const handlePanelChange = (panel: PanelType) => {
    uiLogger.menu('MobileDrawer', panel);
    consoleCtx.setSelectedPanel(panel);
    onClose();
  };

  // Cycle through theme modes
  const cycleTheme = () => {
    const modes: ThemeMode[] = ['light', 'auto', 'dark'];
    const currentIndex = modes.indexOf(themeMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    onThemeModeChange(modes[nextIndex]);
  };

  const getThemeIcon = () => {
    switch (themeMode) {
      case 'light':
        return <LightIcon />;
      case 'dark':
        return <DarkIcon />;
      default:
        return <AutoIcon />;
    }
  };

  const getThemeLabel = () => {
    switch (themeMode) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      default:
        return 'Auto';
    }
  };

  const allMenuItems: { panel: PanelType; label: string; icon: JSX.Element; dividerBefore?: boolean; debugOnly?: boolean }[] = [
    { panel: 'chat', label: 'Play!', icon: <ChatIcon /> },
    { panel: 'history', label: 'History', icon: <HistoryIcon /> },
    { panel: 'game', label: 'Games', icon: <GameIcon /> },
    { panel: 'ship', label: 'Ship & Cargo', icon: <ShipIcon />, dividerBefore: true },
    { panel: 'location', label: 'Current Location', icon: <LocationIcon /> },
    { panel: 'map', label: 'Galaxy Map', icon: <MapIcon /> },
    { panel: 'missions', label: 'Missions', icon: <MissionIcon /> },
    { panel: 'market', label: 'Market', icon: <MarketIcon /> },
    { panel: 'npcs', label: 'Nearby Characters', icon: <NPCsIcon /> },
    { panel: 'repairs', label: 'Repairs', icon: <RepairsIcon /> },
    { panel: 'logs', label: 'System Logs', icon: <LogsIcon />, dividerBefore: true, debugOnly: true },
    { panel: 'db-browser', label: 'D1 Browser', icon: <DbIcon />, debugOnly: true },
    { panel: 'r2-browser', label: 'R2 Storage', icon: <R2Icon />, debugOnly: true },
    { panel: 'ai-console', label: 'AI Console', icon: <PromptIcon />, debugOnly: true },
    { panel: 'pinecone-test', label: 'Pinecone Test', icon: <SearchIcon />, debugOnly: true },
    { panel: 'voice-test', label: 'Voice Test', icon: <VoiceIcon />, debugOnly: true },
    { panel: 'database-admin', label: 'Game Admin', icon: <WarningIcon />, debugOnly: true },
  ];

  // Filter menu items based on debug mode
  const menuItems = allMenuItems.filter(item => !item.debugOnly || consoleCtx.debugMode);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Ship Console
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Navigation & Controls
        </Typography>
      </Box>

      <Divider />

      {/* Navigation - single panel at a time */}
      <List>
        {menuItems.map((item) => (
          <Box key={item.panel}>
            {item.dividerBefore && <Divider sx={{ my: 1 }} />}
            <ListItem disablePadding>
              <ListItemButton
                selected={consoleCtx.selectedPanel === item.panel}
                onClick={() => handlePanelChange(item.panel)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          </Box>
        ))}
      </List>

      <Divider />

      {/* Theme toggle - cycling button */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Theme:
        </Typography>
        <Tooltip title={`${getThemeLabel()} mode (tap to change)`}>
          <IconButton onClick={cycleTheme} size="small">
            {getThemeIcon()}
          </IconButton>
        </Tooltip>
        <Typography variant="body2">{getThemeLabel()}</Typography>
      </Box>

      <Divider />

      {/* Notification Settings */}
      <NotificationSettings />

      <Box sx={{ flexGrow: 1 }} />

      <Divider />

      {/* Info footer */}
      <Box
        sx={{
          p: 2,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
        onDoubleClick={onDebugToggle}
      >
        <Typography variant="caption" color="text.secondary" display="block">
          Space Antics
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Built: {new Date(BUILD_DATE === 'development' ? Date.now() : BUILD_DATE).toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5, fontSize: '0.6rem' }}>
          Double-click to toggle debug panels
        </Typography>
      </Box>
    </Drawer>
  );
}
